import React, { CSSProperties, useRef, useState } from 'react'
import './Info.css'
import cn from 'classnames'
import Icon from '../Icon2/Icon'
import Tooltip from '../Tooltip/Tooltip'
import Button from '../Button/Button'
import { isEnterOrSpacePressed } from 'utils/keyboardUtils'

type InfoType = 'light' | 'dark' | 'success' | 'warning' | 'attention'

interface InfoProps {
  className?: string
  icon?: React.ReactNode
  content: string | React.ReactNode
  type?: InfoType
  borderColor?: string
  backgroundColor?: string
  dismissable?: boolean,
  width?: number | string | undefined,
  leftSpacing?,
  rightSpacing?,
  bottomSpacing?
  topSpacing?
  enableHover?: boolean | undefined,
  style?: CSSProperties,
  contentStyles?: CSSProperties,
  iconStyles?: CSSProperties,
  dismissStyles?: CSSProperties,
  contentClassName?: string,
  iconClassName?: string,
  dismissClassName?: string,
  onCancelClick?: Function,
  testId?: string,
  tabIndex?: number,
  version?: "v2",
  collapsedText?: string,
  collapsedClick?: Function
}

const InfoDismissiable = ({ contentClass, contentStyles, dismissable, content, version, dismissClass, dismissStyles, onDismissClick, tabIndex, handleDismissKeyUp, collapsedText, collapsedClick }) => {
  const [hideInfo, setHideInfo] = useState(collapsedText ? true : false);

  const handleToggle = () => {
    collapsedClick && collapsedClick(hideInfo);
    setHideInfo(!hideInfo)
  }

  return (
    <>
      <div className={contentClass} style={contentStyles}>
        {hideInfo ? <div>{collapsedText}</div> : content}
      </div>

      {dismissable && (
        version !== 'v2' ?
          <div className={dismissClass} style={dismissStyles} onClick={onDismissClick} role='button' onKeyUp={tabIndex > -1 && handleDismissKeyUp} tabIndex={tabIndex}>
            <Tooltip content="Dismiss" position="top" showArrow={false} interactive={false}>
              <Icon icon="Cancel" />
            </Tooltip>
          </div> :
          <Button className="ml-4" onlyIcon={true} version="v2" iconProps={{
            withTooltip: true,
            tooltipContent: "Dismiss",
            tooltipPosition: "top"
          }} onClick={onDismissClick} onKeyUp={tabIndex > -1 && handleDismissKeyUp} icon="v2-CloseNoborder" onlyIconHoverColor="primary" />
      )}

      {collapsedClick && version === 'v2' ? <div className={`Info__collapsable ${hideInfo ? "toggled" : ""}`} onClick={handleToggle} role='button' tabIndex={tabIndex}>
        <Tooltip content={hideInfo ? 'Expand' : 'Collapse'} position="top" showArrow={false} interactive={false}>
          <Icon icon={"CaretDownNew"} version='v2' size='medium' />
        </Tooltip>
      </div> : null}

    </>
  )
}

const Info = (props: InfoProps) => {

  const {
    icon,
    content,
    className,
    type,
    backgroundColor,
    borderColor,
    dismissable,
    width,
    leftSpacing,
    rightSpacing,
    bottomSpacing,
    topSpacing,
    enableHover,
    style = {},
    contentStyles = {},
    iconStyles = {},
    dismissStyles = {},
    contentClassName,
    iconClassName,
    dismissClassName,
    onCancelClick,
    testId = "cs-Info",
    version,
    tabIndex = -1,
    collapsedText,
    collapsedClick
  } = props

  let customStyle: CSSProperties = {}
  const infoEl = useRef(null);

  if (backgroundColor) customStyle.backgroundColor = backgroundColor;
  if (borderColor) customStyle.borderColor = borderColor;
  if (width) customStyle.width = width;
  if (leftSpacing) customStyle.marginLeft = leftSpacing;
  if (rightSpacing) customStyle.marginRight = rightSpacing;
  if (bottomSpacing) customStyle.marginBottom = bottomSpacing;
  if (topSpacing) customStyle.marginTop = topSpacing;

  customStyle = Object.assign(customStyle, style);

  const onDismissClick = () => {
    infoEl.current.remove();
    onCancelClick()
  };

  const handleDismissKeyUp = (e: any): any => {
    if (isEnterOrSpacePressed(e.key)) {
      onDismissClick();
      onCancelClick && onCancelClick()
    }
  }

  const infoClass = cn('Info', [className], {
    [`Info--${type}`]: type,
    [`Info--v2`]: version === "v2",
    [`Info--collapse`]: collapsedClick ? true : false
  })
  const dismissClass = cn('Info__dismiss', [type], { 'hoverable__dismiss': enableHover, }, [dismissClassName])
  const contentClass = cn('Info__content', [type], [contentClassName])
  const iconClass = cn('Info__icon', [iconClassName], { [`Info__icon--${type}`]: type, }, 'flex-h-center', 'flex-v-center')

  return (
    <div
      className={infoClass}
      style={customStyle}
      ref={infoEl}
      data-test-id={testId}
    >
      {icon && <div className={iconClass} style={iconStyles}>{icon}</div>}
      {version === "v2" && <div className="Info__border"></div>}
      {version === "v2" ?
        <div className='flex-v-center flex-h-center'>
          <InfoDismissiable
            contentClass={contentClass}
            contentStyles={contentStyles}
            dismissable={dismissable}
            content={content}
            version={version}
            dismissClass={dismissClass}
            dismissStyles={dismissStyles}
            onDismissClick={onDismissClick}
            tabIndex={tabIndex}
            handleDismissKeyUp={handleDismissKeyUp}
            collapsedText={collapsedText}
            collapsedClick={collapsedClick}
          />
        </div> :
        <InfoDismissiable
          contentClass={contentClass}
          contentStyles={contentStyles}
          dismissable={dismissable}
          content={content}
          version={version}
          dismissClass={dismissClass}
          dismissStyles={dismissStyles}
          onDismissClick={onDismissClick}
          tabIndex={tabIndex}
          handleDismissKeyUp={handleDismissKeyUp}
          collapsedText={collapsedText}
          collapsedClick={collapsedClick}
        />
      }
    </div>
  )
}

export default Info
